import { UploadError } from '@unifyapps/defs/blocks/FileUpload/types';
import type { FileAccessScope } from '@unifyapps/defs/types/fileAccessScope';
import type { QueryMeta } from '@unifyapps/network/utils/fetch';
// Type-only: a value import pulls @uppy/core in for every consumer that needs just the types,
// and Turbopack copies it into each chunk group that reaches it. The constructor lives in
// ./getUppyInstance, which UppyProvider loads with await import().
import type { Uppy, UppyFile, UppyOptions } from '@uppy/core';

export enum CloudStorageProvider {
  NFS = 'nfs',
  S3 = 's3',
  // For azure storage, we use unifyapps as provider
  // unifyapps storage works similar to nfs storage, uses unifyapps backend(instead of unifyapps frontend) to upload to azure storage instead of nfs
  UNIFYAPPS = 'unifyapps',
  // @deprecated - use UNIFYAPPS instead
  AZURE = 'azure',
}

export type CloudStorageProviderDetails = {
  provider: CloudStorageProvider;
  isMultipartUploadAllowed: boolean;
  isPublicUploadAllowed: boolean;
};

export type UppyFileMeta = {
  name?: string;
  type?: string;
  size?: number;
  queryMeta?: QueryMeta;
  cacheControl?: string;
  useExternalApi?: boolean;
  relativePath?: string;
  isImporting?: boolean;
  referenceId?: string;
  previewUrl?: string;
  entityId?: string;
  // Page-instance id captured at upload-start so async uppy callbacks can
  // route state writes back to the correct instance's GSS + dep-graph.
  // Undefined for uploads initiated on the main page (base-store path).
  instanceId?: string;
  publicAcl?: boolean;
  uri?: string;
  /**
   * Indicates whether the file is an asset.
   * If true, it is stored as a standard entity and can be
   * moved between environments using changesets.
   */
  isBuilderAsset?: boolean;
  /**
   * Who is allowed to read the file once uploaded. Produced by getFileUploadMeta
   * from a FileVisibility — never hand-built.
   */
  accessScope?: FileAccessScope;
};

export type UppyFileOptions = UppyOptions<UppyFileMeta, Record<string, never>>;

export type UploadedFile<TMeta extends UppyFileMeta = UppyFileMeta> = UppyFile<
  TMeta,
  Record<string, never>
> & {
  name: string;
  size?: number;
  url?: string;
  fileProgress?: number;
  relativePath?: string;
  error?: UploadError;
  retryable?: boolean;
  uploadResponse?: Record<string, string | undefined>;
};

export type ReferenceIdMatcher = (fileReferenceId: string, hookReferenceId: string) => boolean;

export type UppyManager<File> = {
  adaptUploadFile?: (file: File) => File;
  onFileAdded?: (file: File) => File;
  onProgress?: (file: File) => void;
  onRestrictedFile?: (file: File, error: Error) => void;
  onUploadSuccess?: (
    file: File,
    options: {
      removeFileById: (fileId: string) => void;
    },
  ) => Promise<File>;
  onUploadError?: (
    file: File,
    options: {
      removeFileById: (fileId: string) => void;
    },
  ) => File;
  sourceId?: string;
  referenceId: string;
  shouldHandleFile?: (file: UploadedFile) => boolean;
  initialFiles?: Record<string, unknown>;
  isBuilderAsset?: boolean;
  /** Stamped onto every file this uppy instance adds, overriding per-file meta. */
  accessScope?: FileAccessScope;
  /**
   * Custom matcher for referenceId comparison.
   * Defaults to strict equality (===).
   * Use this for pattern matching like startsWith, endsWith, regex, etc.
   */
  referenceIdMatcher?: ReferenceIdMatcher;
};

export type FilesState<T> = Record<string, T>;

export enum FilesActionType {
  RemoveFile = 'REMOVE_FILE',
  RenameFile = 'RENAME_FILE',
  ClearFiles = 'CLEAR_FILES',
  AddOrUpdateFile = 'ADD_OR_UPDATE_FILE',
  UpdateFile = 'UPDATE_FILE',
  UpdateFiles = 'UPDATE_FILES',
}

export interface RemoveFileAction {
  type: FilesActionType.RemoveFile;
  payload: {
    value: string;
  };
}

export interface RenameFileAction {
  type: FilesActionType.RenameFile;
  payload: {
    uppy: Uppy | null;
    fileId: string;
    name: string;
  };
}

export interface ClearFilesAction {
  type: FilesActionType.ClearFiles;
  payload: {
    referenceId: string;
    referenceIdMatcher?: ReferenceIdMatcher;
  };
}

export interface AddOrUpdateFileAction<T> {
  type: FilesActionType.AddOrUpdateFile;
  payload: {
    fileId: string;
    value: T;
  };
}

export interface UpdateFileAction {
  type: FilesActionType.UpdateFile;
  payload: {
    fileId: string;
    value: { error?: string; retryable?: boolean };
  };
}

export interface UpdateFilesAction<T> {
  type: FilesActionType.UpdateFiles;
  payload: {
    files: Record<string, T>;
  };
}

export type FilesAction<T> =
  | RemoveFileAction
  | RenameFileAction
  | ClearFilesAction
  | AddOrUpdateFileAction<T>
  | UpdateFileAction
  | UpdateFilesAction<T>;

export type BlobUploadParams = {
  blob: Blob;
  name: string;
  type: string;
  meta?: UppyFileMeta;
};

export type { Uppy, UppyFile };
export { UploadError };
